import axiosInstance from '../utils/axiosInstance';

export async function getSubscriptionStatus() {
    const { data } = await axiosInstance.get(`/subscription-plans/status`, { workspace: "admin" });
    return data?.data ?? data;
}

export async function getSubscriptionFeatures() {
    const { data } = await axiosInstance.get(`/subscription-plans/features`, { workspace: "admin" });
    return data?.data ?? data;
}

// Fetch list of plans user can switch to
export async function getUpgradeOptions() {
    const { data } = await axiosInstance.get(`/subscription-plans/upgrade-options`);
     return data?.data ?? data;
}

export async function getActiveSubscription() {
    const { data } = await axiosInstance.get(`/subscription-plans/active`);
     return data?.data ?? data;
}

// Calculate pricing break-up BEFORE ordering payment 
export async function calculatePrice(params) {
    const { data } = await axiosInstance.post(`/subscription-plans/calculate-upgrade`, {}, {params});
     return data?.data ?? data;
}

// Start a prorated upgrade — returns a one-time Razorpay ORDER
// ({ razorpay_order_id, amount, amount_paise, ... }), or { no_charge:true, applied }
// when the prorated difference is ₹0.
export async function createUpgradeSubscription(payload) {
  const { data } = await axiosInstance.post("/subscription-plans/upgrade-subscription", payload);
  return data?.data ?? data;
}

// Confirm a paid upgrade order from the Razorpay callback and switch the plan now
// (idempotent with the order.paid webhook).
export async function confirmUpgrade(payload) {
  const { data } = await axiosInstance.post("/subscription-plans/upgrade-confirm", payload);
  return data?.data ?? data;
}

// Abandon a pending (unpaid) upgrade so a fresh one can be started.
export async function cancelPendingUpgrade() {
  const { data } = await axiosInstance.post("/subscription-plans/upgrade-cancel");
  return data?.data ?? data;
}

// The company's own applied purchases (seat add-ons, plan upgrades, cycle switches).
export async function getPurchaseHistory() {
  const { data } = await axiosInstance.get("/subscription-plans/purchase-history");
  return data?.data ?? data;
}

// After the customer authorizes a replacement autopay mandate (UPI migration),
// link it server-side: cancels the old subscription, attaches the new one.
// Idempotent with the subscription webhooks.
export async function confirmMandateMigration() {
  const { data } = await axiosInstance.post("/subscription-plans/mandate-migration/confirm");
  return data?.data ?? data;
}

export async function createFreeTrialToPaidSubscription(payload) {
  const { data } = await axiosInstance.post("/subscription-plans/free-to-paid", payload);
  return data?.data ?? data;
}

export async function renewSubscription(payload) {
  const { data } = await axiosInstance.post("/subscription-plans/renew-subscription", payload);
  return data?.data ?? data;
}

export async function getAllSubscriptionPlans() {
    const { data } = await axiosInstance.get('/subscription-plans');
    // If your API wraps payload as { data: [...] }, normalize it:
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getSubscriptionPlanById(planId) {
    const { data } = await axiosInstance.get(`/subscription-plans/plans/${planId}`);
    return data?.data ?? data;
}

export async function createSubscriptionPlan(payload) {
    const { data } = await axiosInstance.post('/subscription-plans', payload);
    return data?.data ?? data;
}

export async function updateSubscriptionPlan(planId, payload) {
    const { data } = await axiosInstance.put(`/subscription-plans/${planId}`, payload);
    return data?.data ?? data;
}

export async function patchSubscriptionPlan(planId, payload) {
    const { data } = await axiosInstance.patch(`/subscription-plans/${planId}`, payload);
    return data?.data ?? data;
}

export async function deleteSubscriptionPlan(planId) {
    await axiosInstance.delete(`/subscription-plans/${planId}`);
}

export async function createSignupSubscription(payload){
    const { data } = await axiosInstance.post(`/subscription-plans/payment/signupSubscription`, payload);
    return data;
}

export async function confirmPayment(payload){
    const { data } = await axiosInstance.post(`/subscription-plans/payment/confirmAndActivate`, payload);
    return data;
}

export async function getAccessPermissions() {
    const { data } = await axiosInstance.get(`/subscription-plans/role/permissions`, { workspace: "admin" });
    return data?.data ?? data;
}

// ---------- Mid-cycle employee seat add-ons ----------
// Server-computed prorated price for buying N more seats at the plan's per-user
// rate. The company is taken from the auth token, never the request body.
export async function getSeatAddOnQuote(extraEmployees) {
    const { data } = await axiosInstance.post(`/subscription-plans/employees/quote`, {
        extra_employees: extraEmployees,
    });
    return data?.data ?? data;
}

// Stage the purchase + create a Razorpay ORDER. Grants nothing until the
// order.paid webhook confirms payment. Returns { razorpay_order_id, amount, amount_paise, ... }.
export async function purchaseSeatAddOn(extraEmployees) {
    const { data } = await axiosInstance.post(`/subscription-plans/employees/purchase`, {
        extra_employees: extraEmployees,
    });
    return data?.data ?? data;
}

// Confirm a paid add-on order from the Razorpay callback and apply seats now
// (idempotent with the order.paid webhook).
export async function confirmSeatAddOn(payload) {
    const { data } = await axiosInstance.post(`/subscription-plans/employees/confirm`, payload);
    return data?.data ?? data;
}

export async function getSeatAddOnRequests() {
    const { data } = await axiosInstance.get(`/subscription-plans/employees/requests`);
    return data?.data ?? data;
}

// Retry a failed purchase (seat add-on, plan upgrade, or cycle switch)
export async function retryPurchase(requestId) {
    const { data } = await axiosInstance.post(`/subscription-plans/purchase-retry/${requestId}`);
    return data?.data ?? data;
}
